import React, { useState, useEffect } from 'react'
import {
	Card,
	Form,
	Input,
	Button,
	message,
	Spin,
	Typography,
	Table,
	Space,
	Modal,
	Popconfirm,
	Tooltip,
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { httpGet, httpPost, httpPatch, httpDelete } from '../../common/http.service'

const { Text } = Typography

interface ShiftRange {
	start: string
	end: string
}

interface RegularShift {
	id: string
	name: string
	team: string
	firstShift: ShiftRange
	secondShift?: ShiftRange
	createdAt?: string
}

const RegularShiftManagementTab: React.FC = () => {
	const [form] = Form.useForm()
	const [loading, setLoading] = useState(false)
	const [shifts, setShifts] = useState<RegularShift[]>([])
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [editingShift, setEditingShift] = useState<RegularShift | null>(null)

	useEffect(() => {
		loadShifts()
	}, [])

	const loadShifts = async () => {
		try {
			setLoading(true)
			const response: any = await httpGet('/api/v1/admin/shift/regular')
			if (response?.data) {
				setShifts(response.data)
			}
		} catch (error) {
			console.error('Failed to load regular shifts:', error)
		} finally {
			setLoading(false)
		}
	}

	const handleCreate = () => {
		setEditingShift(null)
		form.resetFields()
		setIsModalVisible(true)
	}

	const handleEdit = (shift: RegularShift) => {
		setEditingShift(shift)
		form.setFieldsValue({
			id: shift.id,
			name: shift.name,
			team: shift.team,
			firstShiftStart: shift.firstShift.start,
			firstShiftEnd: shift.firstShift.end,
			secondShiftStart: shift.secondShift?.start,
			secondShiftEnd: shift.secondShift?.end,
		})
		setIsModalVisible(true)
	}

	const handleDelete = async (id: string) => {
		try {
			await httpDelete(`/api/v1/admin/shift/regular/${id}`)
			message.success('Regular shift deleted successfully')
			loadShifts()
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Failed to delete regular shift')
		}
	}

	const handleSubmit = async (values: any) => {
		try {
			const payload: any = {
				id: values.id,
				team: values.team,
				name: values.name,
				firstShift: {
					start: values.firstShiftStart,
					end: values.firstShiftEnd,
				},
			}

			if (values.secondShiftStart && values.secondShiftEnd) {
				payload.secondShift = {
					start: values.secondShiftStart,
					end: values.secondShiftEnd,
				}
			}

			if (editingShift) {
				await httpPatch('/api/v1/admin/shift/regular', payload)
				message.success('Regular shift updated successfully')
			} else {
				await httpPost('/api/v1/admin/shift/regular', payload)
				message.success('Regular shift created successfully')
			}

			setIsModalVisible(false)
			form.resetFields()
			loadShifts()
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Failed to save regular shift')
		}
	}

	const columns = [
		{
			title: 'Shift ID',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'Shift Name',
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: 'Team',
			dataIndex: 'team',
			key: 'team',
		},
		{
			title: 'First Shift',
			key: 'firstShift',
			render: (record: RegularShift) =>
				`${record.firstShift.start} - ${record.firstShift.end}`,
		},
		{
			title: 'Second Shift',
			key: 'secondShift',
			render: (record: RegularShift) =>
				record.secondShift
					? `${record.secondShift.start} - ${record.secondShift.end}`
					: '-',
		},
		{
			title: 'Action',
			key: 'action',
			render: (record: RegularShift) => (
				<Space size='small'>
					<Tooltip title='Edit shift'>
						<Button
							type='link'
							icon={<EditOutlined />}
							onClick={() => handleEdit(record)}
						>
							Edit
						</Button>
					</Tooltip>
					<Popconfirm
						title='Are you sure you want to delete this shift?'
						onConfirm={() => handleDelete(record.id)}
						okText='Yes'
						cancelText='No'
					>
						<Button type='link' danger icon={<DeleteOutlined />}>
							Delete
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	]

	return (
		<>
			<Card
				title='Regular Shift Management'
				bordered={false}
				style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
				extra={
					<Button type='primary' icon={<PlusOutlined />} onClick={handleCreate}>
						Create Shift
					</Button>
				}
			>
				<Spin spinning={loading}>
					<Table
						columns={columns}
						dataSource={shifts}
						rowKey='id'
						pagination={{ pageSize: 10 }}
						locale={{
							emptyText: (
								<div style={{ padding: '40px 0' }}>
									<Text type='secondary'>
										No regular shifts found. Click "Create Shift" to add one.
									</Text>
								</div>
							),
						}}
					/>
				</Spin>
			</Card>

			<Modal
				title={editingShift ? 'Edit Regular Shift' : 'Create Regular Shift'}
				open={isModalVisible}
				onOk={() => form.submit()}
				onCancel={() => {
					setIsModalVisible(false)
					form.resetFields()
				}}
				width={700}
				okText={editingShift ? 'Update' : 'Create'}
			>
				<Form form={form} layout='vertical' onFinish={handleSubmit}>
					<Form.Item
						name='id'
						label='Shift ID'
						rules={[{ required: true, message: 'Please enter shift ID' }]}
					>
						<Input placeholder='e.g., regular' disabled={!!editingShift} />
					</Form.Item>

					<Form.Item
						name='name'
						label='Shift Name'
						rules={[{ required: true, message: 'Please enter shift name' }]}
					>
						<Input placeholder='e.g., Regular Shift' />
					</Form.Item>

					<Form.Item
						name='team'
						label='Team'
						rules={[{ required: true, message: 'Please enter team name' }]}
					>
						<Input placeholder='e.g., regular, team-a' />
					</Form.Item>

					<Form.Item label='First Shift Time Range' required>
						<Space.Compact style={{ width: '100%' }}>
							<Form.Item
								name='firstShiftStart'
								noStyle
								rules={[{ required: true, message: 'Please enter start time' }]}
							>
								<Input
									placeholder='Start time (e.g., 07:30)'
									style={{ width: '50%' }}
								/>
							</Form.Item>
							<Form.Item
								name='firstShiftEnd'
								noStyle
								rules={[{ required: true, message: 'Please enter end time' }]}
							>
								<Input
									placeholder='End time (e.g., 12:00)'
									style={{ width: '50%' }}
								/>
							</Form.Item>
						</Space.Compact>
					</Form.Item>

					<Form.Item label='Second Shift Time Range (Optional)'>
						<Space.Compact style={{ width: '100%' }}>
							<Form.Item name='secondShiftStart' noStyle>
								<Input
									placeholder='Start time (e.g., 13:00)'
									style={{ width: '50%' }}
								/>
							</Form.Item>
							<Form.Item name='secondShiftEnd' noStyle>
								<Input
									placeholder='End time (e.g., 17:00)'
									style={{ width: '50%' }}
								/>
							</Form.Item>
						</Space.Compact>
					</Form.Item>
				</Form>
			</Modal>
		</>
	)
}

export default RegularShiftManagementTab
